import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, type CanActivateFn } from '@angular/router';
import { map } from 'rxjs';
import { CustomerFeatureFlagsService } from '../services/feature-flags.service';
import { User } from '../models/user';

/**
 * Route guard factory that gates a route behind a feature flag.
 * Waits for flags to load, then checks the given flag.
 * Redirects to the customer dashboard if the flag is not enabled.
 */
export function featureFlagGuard(flagName: string): CanActivateFn {
  return (route: ActivatedRouteSnapshot) => {
    const featureFlags = inject(CustomerFeatureFlagsService);
    const router = inject(Router);
    const user = inject(User);

    /**
     * Load the flags this guard is about to wait on.
     *
     * Without this the guard DEADLOCKS on any direct load of a gated route —
     * refresh, bookmark, an emailed link, or a browser back into one.
     *
     * `hasFlagAsync` waits for `whenReady$`, which only emits once `loadFlags`
     * has run, and the only caller of `loadFlags` was `AppLayoutComponent`.
     * Angular resolves every guard in a path BEFORE activating any component in
     * it, so the layout that fetches the flags cannot render until the guard
     * that needs them passes. Nothing ever breaks the cycle: the page stays
     * blank indefinitely — no spinner, no error, no redirect. Reproduced on
     * `/customer/cases`, which sat empty through a 15-second wait and was
     * confirmed on dev by refreshing the page.
     *
     * It only appeared to work because users normally arrive from another
     * customer page, where the layout had already loaded the flags.
     *
     * A guard must not depend on a component to fetch what it gates on, so it
     * fetches them itself. `loadFlags` is idempotent per org — when the layout
     * has already loaded them this returns immediately and nothing refetches.
     */
    const orgId =
      Number(route.queryParamMap.get('organizationId')) ||
      user.primary_organization?.id;

    // No resolvable org means the flags can never load, so waiting on them is
    // the same deadlock by another route. Fail to the dashboard instead: a
    // wrong redirect is recoverable, a permanently blank page is not.
    if (!orgId) return router.createUrlTree(['/customer/customer-dashboard']);

    featureFlags.loadFlags(orgId);

    return featureFlags.hasFlagAsync(flagName).pipe(
      map((enabled) => enabled || router.createUrlTree(['/customer/customer-dashboard'])),
    );
  };
}
